import {
  aDosDecimales,
  dividirDinero,
  multiplicarDinero,
  redondearDinero,
  restarDinero,
  sumarDinero,
} from '../utils/dinero';
import {
  clasificarHorasExtrasPorJornada,
  CODIGO_AFECTACION_TRIBUTOS_HE,
  CODIGO_PLAME_HE_CONSU_60,
  CODIGO_PLAME_HE_CONSU_100,
  DESCRIPCION_HE_CONSU_60,
  DESCRIPCION_HE_CONSU_100,
  ORIGEN_INGRESO_HE_CONSU,
  codigoInternoHorasExtraConsuPorClave,
  esCodigoInternoHorasExtraConsu,
  esDiaEspecialHorasExtras,
} from '../utils/horasExtrasConstruccionCivil';

function esCodigoConstruccionCivil(valor) {
  const texto = String(valor || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (!texto) return false;
  return texto === 'CONSU'
    || texto === 'CONSTRUCCION'
    || texto.includes('CONSTRUCC');
}

export function esRegimenConstruccionCivil(empleado, regimenes = []) {
  const crudo = empleado?.datosSalariales?.regimenLaboral
    || empleado?.datosEmpleado?.regimen_laboral?.codigo
    || empleado?.regimen_laboral?.codigo
    || empleado?.regimenLaboral
    || empleado?.regimen_laboral_codigo
    || empleado?.regimen_laboral?.nombre
    || empleado?.datosEmpleado?.regimen_laboral?.nombre
    || '';
  if (esCodigoConstruccionCivil(crudo)) return true;

  const lista = regimenes.length
    ? regimenes
    : (empleado?.datosAPIs?.regimenesLaborales || []);
  const id = empleado?.regimen_laboral_id
    ?? empleado?.datosEmpleado?.regimen_laboral_id
    ?? empleado?.regimenLaboralId
    ?? empleado?.regimen_laboral?.id
    ?? empleado?.datosEmpleado?.regimen_laboral?.id
    ?? null;
  const hallado = lista.find((r) =>
    (crudo && (r.nombre === crudo || r.codigo === crudo))
    || (id != null && Number(r.id) === Number(id))
  );
  return esCodigoConstruccionCivil(hallado?.codigo)
    || esCodigoConstruccionCivil(hallado?.nombre);
}

function fechaIso(valor) {
  if (!valor) return null;
  return String(valor).split('T')[0];
}

function enumerarDias(inicio, fin) {
  const out = [];
  const a = new Date(`${fechaIso(inicio)}T00:00:00`);
  const b = new Date(`${fechaIso(fin)}T00:00:00`);
  for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    out.push(`${y}-${m}-${day}`);
  }
  return out;
}

function mesDe(fecha) {
  return Number(String(fecha).slice(5, 7));
}

function periodoGrati(fecha) {
  return mesDe(fecha) <= 7 ? 'fp' : 'navidad';
}

export function resolverNetoPorDiaConstruccion({
  neto = 0,
  diasAsistencia = 0,
  diasTrabajados = 0,
  diasComputables = 0,
} = {}) {
  const dias = [diasAsistencia, diasComputables, diasTrabajados]
    .map((d) => Number(d) || 0)
    .find((d) => d > 0) || 0;
  if (dias <= 0) {
    return { netoPorDia: 0, diasNeto: 0 };
  }
  return {
    netoPorDia: aDosDecimales(dividirDinero(neto, dias)),
    diasNeto: dias,
  };
}

export function diasParaNetoConstruccion({
  resumen = {},
  detalleDias = [],
  diasTrabajadosTareo = 0,
} = {}) {
  const asistencia = Number(resumen?.diasAsistencia) || 0;
  if (asistencia > 0) return asistencia;
  const computables = Number(resumen?.diasComputables) || 0;
  if (computables > 0) return computables;
  if (Array.isArray(detalleDias) && detalleDias.length) {
    const asistidos = detalleDias.filter((d) => d?.asistio).length;
    if (asistidos > 0) return asistidos;
    const conRegistro = detalleDias.filter((d) => !d?.falta && !d?.sinRegistro).length;
    if (conRegistro > 0) return conRegistro;
  }
  return Number(diasTrabajadosTareo) || 0;
}

export function resolverPeriodoGratificacionConsu(forzado, fecha) {
  const n = String(forzado || '').toLowerCase().trim();
  if (n === 'fp' || n === 'fiestas_patrias' || n === 'enero_julio') return 'fp';
  if (n === 'navidad' || n === 'agosto_diciembre') return 'navidad';
  return periodoGrati(fecha);
}

/** Campo AFP en BD: 10 = 10%, 1.55 = 1,55%. */
export function porcentajePlameADecimal(valor, defecto = 0) {
  const n = Number(valor);
  if (!Number.isFinite(n) || n < 0) {
    return defecto;
  }
  if (n === 0) {
    return 0;
  }
  return n > 1 ? n / 100 : n;
}

export function resolverRegistroAfpEnLista(afpNombreCortoOCompleto, afps = [], afpId = null) {
  const lista = afps || [];
  const idNum = Number(afpId);
  if (Number.isInteger(idNum) && idNum > 0) {
    const porId = lista.find((a) => Number(a.id) === idNum);
    if (porId) return porId;
  }

  const crudo = String(afpNombreCortoOCompleto || '').trim();
  if (!crudo) {
    return null;
  }
  const sinPrefijo = (texto) => String(texto || '').trim().replace(/^AFP\s+/i, '');
  return lista.find((a) => {
    const nombre = String(a.nombre || '').trim();
    return nombre === crudo
      || nombre === `AFP ${crudo}`
      || sinPrefijo(nombre) === sinPrefijo(crudo);
  }) || null;
}

/** Resuelve el registro AFP desde datos salariales / empleado (nombre, id u objeto embebido). */
export function resolverRegistroAfpDesdeContexto({
  afpNombre,
  afpId,
  afpObjeto,
  afps = [],
} = {}) {
  if (afpObjeto && typeof afpObjeto === 'object' && (afpObjeto.seguro != null || afpObjeto.comision_fija != null)) {
    return afpObjeto;
  }
  return resolverRegistroAfpEnLista(afpNombre, afps, afpId);
}

/** PLAME 0608 en CONSU: solo aporte obligatorio (10%), no comisión ni seguro. */
export function tasaAporteObligatorio0608Construccion(afpRegistro) {
  if (!afpRegistro) {
    return 0.1;
  }
  return porcentajePlameADecimal(
    afpRegistro.aporte ?? afpRegistro.aporte_obligatorio,
    0.1,
  );
}

export function tasasAfpConstruccion(afpRegistro, tipoAfp = 'Flujo') {
  if (!afpRegistro) {
    return { comision: 0, seguro: 0, aporte: 0.1 };
  }
  const tipo = String(tipoAfp || 'Flujo').toLowerCase();
  const comisionPct = tipo.includes('mixto') || tipo.includes('saldo')
    ? Number(afpRegistro.comision_variable || 0)
    : Number(afpRegistro.comision_fija || afpRegistro.comision || 0);
  return {
    comision: porcentajePlameADecimal(comisionPct, 0),
    seguro: porcentajePlameADecimal(afpRegistro.seguro, 0),
    aporte: tasaAporteObligatorio0608Construccion(afpRegistro),
  };
}

function edadEmpleadoDesdeFechaNacimiento(fechaNacimiento) {
  if (!fechaNacimiento) {
    return null;
  }
  const nacimiento = new Date(fechaNacimiento);
  if (Number.isNaN(nacimiento.getTime())) {
    return null;
  }
  const hoy = new Date();
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const cumple = new Date(hoy.getFullYear(), nacimiento.getMonth(), nacimiento.getDate());
  if (hoy < cumple) {
    edad -= 1;
  }
  return edad;
}

export function normalizarCodigoPlame(codigo) {
  const raw = String(codigo || '').trim();
  if (!raw) return '';
  return /^\d+$/.test(raw) ? raw.padStart(4, '0') : raw;
}

export function buscarEnTablaPlame(conceptosPlame = [], codigo) {
  const objetivo = normalizarCodigoPlame(codigo);
  if (!objetivo) return null;
  return (conceptosPlame || []).find((c) => {
    const actual = normalizarCodigoPlame(c?.codigo_plame || c?.codigo);
    return actual === objetivo;
  }) || null;
}

export function codigoConsuDesdeMapa(plameConsu, clave, fallback) {
  if (!clave) return fallback;
  if (Array.isArray(plameConsu)) {
    const row = plameConsu.find((r) => r?.concepto_clave === clave);
    return row?.codigo_plame || row?.codigo || fallback;
  }
  const row = plameConsu?.[clave] || plameConsu?.get?.(clave);
  return row?.codigo_plame || row?.codigo || fallback;
}

function codigoPlame(mapa, clave, fallback) {
  const codigo = codigoConsuDesdeMapa(mapa, clave, fallback);
  const row = Array.isArray(mapa)
    ? mapa.find((r) => r?.concepto_clave === clave)
    : (mapa?.[clave] || mapa?.get?.(clave));
  const exportable = row?.validado_sunat !== false && Boolean(codigo);
  return { codigo, exportable, clave };
}

function linea({
  clave,
  codigo,
  descripcion,
  base,
  formula,
  resultado,
  exportable = true,
  grupo = null,
  origen = 'construccion_civil',
  plame_configuracion_id = null,
  plame_configuracion = null,
  codigo_afectacion_tributos = null,
  plame_configuracion_tributos = null,
}) {
  const monto = aDosDecimales(resultado);
  return {
    codigoPlame: codigo,
    codigo_plame: codigo,
    codigo,
    concepto: descripcion,
    descripcion,
    nombre: descripcion,
    monto,
    base: aDosDecimales(base),
    base_calculo: aDosDecimales(base),
    formula,
    resultado: monto,
    exportable_plame: exportable && Boolean(codigo),
    grupo,
    origenCalculo: origen,
    claveConstruccion: clave,
    tipo_concepto: 'ingreso',
    plame_configuracion_id,
    plame_configuracion,
    codigo_afectacion_tributos,
    plame_configuracion_tributos,
  };
}

function mapaTareos(tareos) {
  const map = {};
  (tareos || []).forEach((t) => {
    const f = fechaIso(t.fecha || t.fecha_tareo);
    if (!f) return;
    map[f] = t;
  });
  return map;
}

function horasJornadaDia(tareo, horasDefault) {
  const h = Number(tareo?.horas_jornada || tareo?.horas_turno || horasDefault || 8);
  return h > 0 ? h : 8;
}

function horasOrdinarias(tareo, horasDefault) {
  if (!tareo) return 0;
  if (tareo.horas_ordinarias != null) return Number(tareo.horas_ordinarias) || 0;
  if (tareo.horas_trabajadas != null) return Number(tareo.horas_trabajadas) || 0;
  if (tareo.horas != null) return Number(tareo.horas) || 0;
  const evento = String(tareo.tipo_evento || tareo.evento || '').toLowerCase();
  if (['falta', 'inasistencia', 'descanso', 'feriado'].includes(evento)) return 0;
  return horasDefault;
}

function esFalta(tareo) {
  const evento = String(tareo?.tipo_evento || tareo?.evento || '').toLowerCase();
  return ['falta', 'inasistencia'].includes(evento) || tareo?.falta === true;
}

function esDescanso(tareo) {
  const evento = String(tareo?.tipo_evento || tareo?.evento || '').toLowerCase();
  return evento.includes('descanso') || tareo?.descanso === true;
}

function esFeriado(tareo) {
  const evento = String(tareo?.tipo_evento || tareo?.evento || '').toLowerCase();
  return evento.includes('feriado') || tareo?.feriado === true || tareo?.es_feriado === true;
}

function esDomingoIso(fecha) {
  return new Date(`${fecha}T12:00:00`).getDay() === 0;
}

function lunesDeSemana(fecha) {
  const d = new Date(`${fecha}T12:00:00`);
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offset);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function adelantoTipo(derechos, tipo, periodo) {
  return (derechos || [])
    .filter((d) => d.tipo === tipo && (!periodo || d.periodo === periodo))
    .reduce((acc, d) => sumarDinero(acc, d.pagado || 0), 0);
}

/**
 * Motor diario CONSU. No usa /30 del régimen general.
 */
export function calcularRemuneracionPeriodoConstruccion({
  fechaInicio,
  fechaFin,
  parametro,
  ficha,
  tareos = [],
  feriados = [],
  hijosConDerecho = 0,
  derechosPrevios = [],
  plame = {},
  conceptosPlame = [],
  fechaIngreso = null,
  fechaCese = null,
  periodoGratificacion = null,
}) {
  if (!parametro) {
    return { error: 'No hay parámetro vigente de construcción civil para la categoría y fecha.' };
  }
  if (!ficha?.categoria) {
    return { error: 'Falta asignar la categoría (operario, oficial o peón). El régimen no se calcula como planilla mensual.' };
  }

  const jornal = Number(parametro.jornal_basico);
  const horasStd = Number(parametro.horas_jornada || 8);
  const bucPct = Number(parametro.buc_porcentaje);
  const movilidadDia = Number(parametro.movilidad_dia);
  const modalidad = ficha.modalidad_beneficios || 'sin_beneficios';
  const conBeneficios = modalidad === 'con_beneficios';
  const mapa = mapaTareos(tareos);
  const feriadoSet = new Set((feriados || []).map((f) => fechaIso(f.fecha || f)));

  const dias = enumerarDias(fechaInicio, fechaFin).filter((f) => {
    if (fechaIngreso && f < fechaIso(fechaIngreso)) return false;
    if (fechaCese && f > fechaIso(fechaCese)) return false;
    return true;
  });

  if (!dias.length) {
    return { error: 'El trabajador no tiene días computables en el periodo.' };
  }

  let jornalBase = 0;
  let dsoBase = 0;
  let movilidadBase = 0;
  let heSimple = 0;
  let he60 = 0;
  let he100 = 0;
  let recargoFeriado = 0;
  let feriadoNoLaborado = 0;
  let especialidadBase = 0;
  let diasAsistencia = 0;
  let diasComputables = 0;
  let diasCalendarioVinculo = 0;
  let diasComputablesGratificacion = 0;
  const detalleDias = [];
  const semanasConAsistencia = new Set();
  const descansosSinTareo = [];

  dias.forEach((fecha) => {
    diasCalendarioVinculo += 1;
    const tareo = mapa[fecha];
    const domingo = esDomingoIso(fecha);
    if (!tareo) {
      const feriadoCal = feriadoSet.has(fecha);
      if (domingo || feriadoCal) {
        descansosSinTareo.push({ index: detalleDias.length, semana: lunesDeSemana(fecha) });
      }
      detalleDias.push({
        fecha,
        asistio: false,
        falta: false,
        descanso: domingo,
        feriado: feriadoCal,
        sinRegistro: true,
        computableGratificacion: false,
        fraccion: 0,
        jornal: 0,
        dso: 0,
        movilidad: 0,
      });
      return;
    }
    const feriado = esFeriado(tareo) || feriadoSet.has(fecha);
    const falta = esFalta(tareo);
    const descanso = esDescanso(tareo) || (domingo && !falta);
    const jornada = horasJornadaDia(tareo, horasStd);
    const ordinarias = falta ? 0 : horasOrdinarias(tareo, descanso || feriado ? 0 : jornada);
    const fraccion = jornada ? ordinarias / jornada : 0;
    const asistio = !falta && fraccion > 0 && !descanso && !(feriado && fraccion === 0);
    const computable = !falta;
    const computableGratificacion = !falta;

    if (computable) diasComputables += 1;
    if (computableGratificacion) diasComputablesGratificacion += 1;

    let jornalDia = 0;
    let dsoDia = 0;
    let movDia = 0;
    let feriadoPago = 0;
    let recargo = 0;

    if (asistio) {
      jornalDia = multiplicarDinero(jornal, fraccion);
      dsoDia = dividirDinero(multiplicarDinero(jornal, fraccion), 6);
      movDia = multiplicarDinero(movilidadDia, fraccion > 0 ? 1 : 0);
      diasAsistencia += fraccion;
      semanasConAsistencia.add(lunesDeSemana(fecha));
    } else if (feriado && !asistio && !falta) {
      feriadoPago = jornal;
    }

    if (feriado && asistio) {
      recargo = jornalDia;
    }

    const diaEspecialHe = esDiaEspecialHorasExtras({ fecha, tareo });
    const heS = Number(tareo?.horas_extra_simple || tareo?.he_simple || 0);
    let heM = Number(tareo?.horas_extra_60 || tareo?.he_60 || 0);
    let heC = Number(tareo?.horas_extra_100 || tareo?.he_100 || 0);
    if (heM <= 0 && heC <= 0) {
      const totalExtra = Number(tareo?.horas_extras ?? tareo?.horas_extra ?? 0);
      if (totalExtra > 0) {
        const clas = clasificarHorasExtrasPorJornada({
          horasExtra: totalExtra,
          diaEspecial: diaEspecialHe,
        });
        heM = clas.he60;
        heC = clas.he100;
      }
    } else if (diaEspecialHe && heM > 0) {
      heC += heM;
      heM = 0;
    }
    const valorHora = dividirDinero(jornal, jornada);
    const heSMonto = multiplicarDinero(valorHora, heS * Number(parametro.he_simple_factor || 1));
    const heMMonto = multiplicarDinero(valorHora, heM * Number(parametro.he_60_factor || 1.6));
    const heCMonto = multiplicarDinero(valorHora, heC * Number(parametro.he_100_factor || 2));

    let espDia = 0;
    if (ficha.especialidad && ficha.tiene_sustento_especialidad) {
      if (asistio && Number(ficha.especialidad.monto_dia)) {
        espDia = Number(ficha.especialidad.monto_dia);
      } else if (asistio && Number(ficha.especialidad.porcentaje)) {
        espDia = multiplicarDinero(jornalDia, Number(ficha.especialidad.porcentaje));
      }
    }

    jornalBase = sumarDinero(jornalBase, jornalDia);
    dsoBase = sumarDinero(dsoBase, dsoDia);
    movilidadBase = sumarDinero(movilidadBase, movDia);
    feriadoNoLaborado = sumarDinero(feriadoNoLaborado, feriadoPago);
    recargoFeriado = sumarDinero(recargoFeriado, recargo);
    heSimple = sumarDinero(heSimple, heSMonto);
    he60 = sumarDinero(he60, heMMonto);
    he100 = sumarDinero(he100, heCMonto);
    especialidadBase = sumarDinero(especialidadBase, espDia);

    detalleDias.push({
      fecha,
      asistio,
      falta,
      descanso,
      feriado,
      computableGratificacion,
      fraccion: redondearDinero(fraccion, 4),
      jornal: aDosDecimales(jornalDia),
      dso: aDosDecimales(dsoDia),
      movilidad: aDosDecimales(movDia),
    });
  });

  descansosSinTareo.forEach(({ index, semana }) => {
    if (!semanasConAsistencia.has(semana)) return;
    diasComputablesGratificacion += 1;
    if (detalleDias[index]) detalleDias[index].computableGratificacion = true;
  });

  const buc = multiplicarDinero(jornalBase, bucPct);
  const descansoFeriado = sumarDinero(dsoBase, feriadoNoLaborado);
  const tipoGrati = resolverPeriodoGratificacionConsu(periodoGratificacion, dias[0]);
  const divisorGrati = tipoGrati === 'fp'
    ? Number(parametro.grati_divisor_fp || 7)
    : Number(parametro.grati_divisor_navidad || 5);
  const diarioGrati = dividirDinero(
    dividirDinero(multiplicarDinero(Number(parametro.grati_jornales || 40), jornal), divisorGrati),
    Number(parametro.grati_divisor_mes || 30)
  );
  const gratiBruta = multiplicarDinero(diarioGrati, diasComputablesGratificacion);
  const tasaBonif = ficha.afiliacion_salud === 'eps'
    ? Number(parametro.bonif_extra_eps || 0.0675)
    : Number(parametro.bonif_extra_essalud || 0.09);
  const bonifExtraBruta = multiplicarDinero(gratiBruta, tasaBonif);
  const periodoClave = `${String(dias[0]).slice(0, 7)}-${tipoGrati}`;
  const adelantoGrati = adelantoTipo(derechosPrevios, tipoGrati === 'fp' ? 'grati_fp' : 'grati_navidad', periodoClave);
  const gratiPagar = Math.max(0, restarDinero(gratiBruta, adelantoGrati));
  const bonifPagar = multiplicarDinero(gratiPagar, tasaBonif);

  const baseIts = sumarDinero(jornalBase, heSimple, he60, he100);
  const itsTotal = multiplicarDinero(baseIts, Number(parametro.its_porcentaje || 0.15));
  const itsCts = multiplicarDinero(baseIts, Number(parametro.its_cts_porcentaje || 0.12));
  const itsUtil = multiplicarDinero(baseIts, Number(parametro.its_utilidades_porcentaje || 0.03));
  const vacaciones = multiplicarDinero(jornalBase, Number(parametro.vacaciones_porcentaje || 0.10));

  const escolaridadAnual = multiplicarDinero(jornal, Number(parametro.escolaridad_jornales || 30));
  const escolaridadDiaria = dividirDinero(escolaridadAnual, 360);
  const escolaridadPeriodo = multiplicarDinero(
    multiplicarDinero(escolaridadDiaria, diasComputablesGratificacion),
    hijosConDerecho
  );
  const pagaEscolaridadAqui = hijosConDerecho > 0 && !ficha.escolaridad_pago_mensual;

  const ingresos = [];
  const push = (opts) => {
    const meta = codigoPlame(plame, opts.clave, opts.codigo);
    const codigo = meta.codigo || opts.codigo;
    const fila = buscarEnTablaPlame(conceptosPlame, codigo);
    ingresos.push(linea({
      ...opts,
      codigo: fila?.codigo_plame || codigo,
      descripcion: fila?.descripcion || opts.descripcion,
      exportable: meta.exportable,
      plame_configuracion_id: fila?.id || null,
      plame_configuracion: fila ? {
        id: fila.id,
        codigo_plame: fila.codigo_plame || codigo,
        descripcion: fila.descripcion,
      } : null,
    }));
  };
  const pushSiHayMonto = (opts) => {
    if (aDosDecimales(opts.resultado) <= 0) return;
    push(opts);
  };
  const filaTributosHe = buscarEnTablaPlame(conceptosPlame, CODIGO_AFECTACION_TRIBUTOS_HE);
  const pushHeConsu = (opts) => {
    if (aDosDecimales(opts.resultado) <= 0) return;
    const meta = codigoPlame(plame, opts.clave, opts.codigo);
    const codigoInterno = codigoInternoHorasExtraConsuPorClave(opts.clave)
      || codigoInternoHorasExtraConsuPorClave('horas_extras_60');
    ingresos.push(linea({
      ...opts,
      codigo: codigoInterno,
      descripcion: opts.descripcion,
      origen: ORIGEN_INGRESO_HE_CONSU,
      exportable: meta.exportable,
      plame_configuracion_id: null,
      plame_configuracion: null,
      codigo_afectacion_tributos: CODIGO_AFECTACION_TRIBUTOS_HE,
      plame_configuracion_tributos: filaTributosHe ? {
        id: filaTributosHe.id,
        codigo_plame: filaTributosHe.codigo_plame || CODIGO_AFECTACION_TRIBUTOS_HE,
        descripcion: filaTributosHe.descripcion,
      } : null,
    }));
  };

  push({
    clave: 'jornal',
    codigo: '0121',
    descripcion: 'Jornal básico',
    base: jornalBase,
    formula: `${jornal} × tiempo ordinario / jornada del día`,
    resultado: jornalBase,
  });
  push({
    clave: 'descanso_feriado',
    codigo: '0115',
    descripcion: 'Descanso semanal y feriados no laborados',
    base: descansoFeriado,
    formula: 'Un jornal por semana completa: jornal × días trabajados / 6. El domingo no se vuelve a pagar. Feriado no laborado = jornal aparte',
    resultado: descansoFeriado,
  });
  pushSiHayMonto({
    clave: 'recargo_feriado',
    codigo: '0105',
    descripcion: 'Recargo por trabajo en feriado',
    base: recargoFeriado,
    formula: 'Jornal del feriado trabajado (no es 0115)',
    resultado: recargoFeriado,
  });
  push({
    clave: 'buc',
    codigo: '0311',
    descripcion: 'Bonificación unificada de construcción',
    base: jornalBase,
    formula: `${aDosDecimales(bucPct * 100)}% sobre jornal básico (no sobre el bruto)`,
    resultado: buc,
  });
  push({
    clave: 'movilidad',
    codigo: '0909',
    descripcion: 'Movilidad condicionada a asistencia',
    base: diasAsistencia,
    formula: `${movilidadDia} × días de asistencia`,
    resultado: movilidadBase,
  });
  if (heSimple > 0) {
    pushHeConsu({
      clave: 'horas_extras_simple',
      codigo: CODIGO_PLAME_HE_CONSU_60,
      descripcion: 'HORAS EXTRAS SIMPLES (CONSTRUCCIÓN CIVIL)',
      base: heSimple,
      formula: '(jornal/jornada) × horas × factor simple',
      resultado: heSimple,
    });
  }
  pushHeConsu({
    clave: 'horas_extras_60',
    codigo: CODIGO_PLAME_HE_CONSU_60,
    descripcion: DESCRIPCION_HE_CONSU_60,
    base: he60,
    formula: `(jornal/${horasStd}) × horas × ${parametro.he_60_factor || 1.6}`,
    resultado: he60,
  });
  pushHeConsu({
    clave: 'horas_extras_100',
    codigo: CODIGO_PLAME_HE_CONSU_100,
    descripcion: DESCRIPCION_HE_CONSU_100,
    base: he100,
    formula: `(jornal/${horasStd}) × horas × ${parametro.he_100_factor || 2}`,
    resultado: he100,
  });
  pushSiHayMonto({
    clave: 'especialidad',
    codigo: '0314',
    descripcion: 'Bonificación de especialidad / altura',
    base: especialidadBase,
    formula: 'Solo si hay parámetro y sustento',
    resultado: especialidadBase,
  });

  const derechos = [];
  const registrarDerecho = (tipo, generado, codigo, exportable, extra = {}) => {
    derechos.push({
      tipo,
      periodo: periodoClave,
      generado: aDosDecimales(generado),
      exigible: aDosDecimales(generado),
      pagado: 0,
      saldo: aDosDecimales(generado),
      codigo_plame: codigo,
      exportable_plame: exportable,
      ...extra,
    });
  };

  push({
    clave: 'cts',
    codigo: '0904',
    descripcion: 'Compensación por tiempo de servicios',
    base: baseIts,
    formula: `${aDosDecimales((parametro.its_cts_porcentaje || 0.12) * 100)}% sobre jornal + HE (ITS-CTS)`,
    resultado: itsCts,
    grupo: 'its_cts',
  });
  push({
    clave: 'its_utilidades',
    codigo: '0910',
    descripcion: 'Participación en las utilidades',
    base: baseIts,
    formula: `${aDosDecimales((parametro.its_utilidades_porcentaje || 0.03) * 100)}% sobre jornal + HE (no es el 15% completo)`,
    resultado: itsUtil,
    grupo: 'its_utilidades',
  });

  if (conBeneficios) {
    push({
      clave: 'remuneracion_vacacional',
      codigo: '0118',
      descripcion: 'Vacaciones 10% (pago a cuenta semanal)',
      base: jornalBase,
      formula: '10% sobre jornal básico del periodo',
      resultado: vacaciones,
    });
    push({
      clave: 'gratificacion',
      codigo: tipoGrati === 'fp' ? '0406' : '0406',
      descripcion: tipoGrati === 'fp' ? 'Gratificación Fiestas Patrias' : 'Gratificación Navidad',
      base: diarioGrati,
      formula: `40 × jornal ÷ ${divisorGrati} ÷ 30 × ${diasComputablesGratificacion} días de vínculo`,
      resultado: gratiPagar,
    });
    push({
      clave: 'bonif_extra_grati',
      codigo: '0312',
      descripcion: 'Bonificación extraordinaria Ley 30334',
      base: gratiPagar,
      formula: `${aDosDecimales(tasaBonif * 100)}% sobre gratificación (${ficha.afiliacion_salud || 'essalud'})`,
      resultado: bonifPagar,
    });
  } else if (diasComputablesGratificacion > 0 || diasAsistencia > 0) {
    registrarDerecho('vacaciones', vacaciones, '0118', true);
    registrarDerecho(tipoGrati === 'fp' ? 'grati_fp' : 'grati_navidad', gratiBruta, '0406', true);
    registrarDerecho('bonif_extra_grati', bonifExtraBruta, '0312', true);
  }

  if (pagaEscolaridadAqui) {
    push({
      clave: 'escolaridad',
      codigo: '0211',
      descripcion: 'Escolaridad de construcción civil',
      base: escolaridadDiaria,
      formula: `(30 × jornal / 360) × ${diasComputablesGratificacion} días de vínculo × ${hijosConDerecho} hijos`,
      resultado: escolaridadPeriodo,
    });
  } else if (hijosConDerecho > 0 && (diasComputablesGratificacion > 0 || diasAsistencia > 0)) {
    registrarDerecho('escolaridad', escolaridadPeriodo, '0211', true, {
      detalle: 'Pago mensual: no se incluye en la semana',
    });
  }

  const montosHePeriodo = sumarDinero(heSimple, he60, he100);
  const baseOnp = conBeneficios
    ? sumarDinero(jornalBase, descansoFeriado, buc, vacaciones, montosHePeriodo)
    : sumarDinero(jornalBase, descansoFeriado, buc, montosHePeriodo);
  const baseConafovicer = sumarDinero(jornalBase, descansoFeriado);
  const baseEssalud = baseOnp;

  const descuentosUsuario = [];
  const aportesUsuario = [];

  return {
    error: null,
    ingresos,
    descuentos: descuentosUsuario,
    aportes: aportesUsuario,
    derechos,
    detalleDias,
    bases: {
      onp_afp: aDosDecimales(baseOnp),
      conafovicer: aDosDecimales(baseConafovicer),
      essalud: aDosDecimales(baseEssalud),
      quinta: aDosDecimales(sumarDinero(baseOnp, movilidadBase)),
      jornal: aDosDecimales(jornalBase),
    },
    parametrosUsados: {
      categoria: ficha.categoria,
      modalidad,
      jornal,
      bucPct,
      movilidadDia,
      fuente: parametro.fuente_normativa,
      vigente_desde: parametro.vigente_desde,
    },
    resumen: {
      diasAsistencia: redondearDinero(diasAsistencia, 4),
      diasComputables,
      diasCalendarioVinculo,
      diasDescansoRemunerado: jornal > 0
        ? redondearDinero(dividirDinero(dsoBase, jornal), 4)
        : 0,
      diasComputablesGratificacion,
      itsTotal: aDosDecimales(itsTotal),
      itsCts: aDosDecimales(itsCts),
      itsUtil: aDosDecimales(itsUtil),
      tipoGratificacion: tipoGrati,
      divisorGratificacion: divisorGrati,
      fechaInicio: fechaIso(fechaInicio),
      fechaFin: fechaIso(fechaFin),
    },
  };
}

const CLAVES_CATALOGO_DIARIO = [
  { clave: 'jornal', fallback: '0121', tipo: 'ingreso', descripcion: 'REMUNERACIÓN O JORNAL BÁSICO' },
  { clave: 'descanso_feriado', fallback: '0115', tipo: 'ingreso', descripcion: 'REMUNERACIÓN EN DÍA DE DESCANSO Y FERIADOS (INCLUIDA LA DEL 1° DE MAYO)' },
  { clave: 'buc', fallback: '0311', tipo: 'ingreso', descripcion: 'BONIFICACIÓN UNIFICADA DE CONSTRUCCIÓN' },
  { clave: 'movilidad', fallback: '0909', tipo: 'ingreso', descripcion: 'MOVILIDAD SUPEDITADA A ASISTENCIA Y QUE CUBRE SÓLO EL TRASLADO' },
  { clave: 'cts', fallback: '0904', tipo: 'ingreso', descripcion: 'COMPENSACIÓN POR TIEMPO DE SERVICIOS' },
  { clave: 'its_utilidades', fallback: '0910', tipo: 'ingreso', descripcion: 'PARTICIPACIÓN EN LAS UTILIDADES - PAGADAS ANTES DE LA DECLARACIÓN ANUAL DEL IMPUESTO A LA RENTA' },
];

export function catalogoDiarioConstruccion(opciones = 'ONP') {
  const opts = typeof opciones === 'string' ? { sistemaPensionario: opciones } : (opciones || {});
  const sistemaPensionario = opts.sistemaPensionario || 'ONP';
  const conceptosPlame = opts.conceptosPlame || [];
  const plameConsu = opts.plameConsu || [];
  const esAfp = String(sistemaPensionario || '').toUpperCase().includes('AFP');
  const resolver = (clave, fallback, descripcionFallback) => {
    const codigo = codigoConsuDesdeMapa(plameConsu, clave, fallback);
    const fila = buscarEnTablaPlame(conceptosPlame, codigo);
    const codigoFinal = fila?.codigo_plame || codigo || fallback;
    return {
      clave,
      codigo: codigoFinal,
      descripcion: fila?.descripcion || descripcionFallback,
      plame_configuracion_id: fila?.id || null,
      plame_configuracion: fila ? {
        id: fila.id,
        codigo_plame: fila.codigo_plame || codigoFinal,
        descripcion: fila.descripcion,
      } : null,
    };
  };
  return {
    ingresos: CLAVES_CATALOGO_DIARIO.map((i) => resolver(i.clave, i.fallback, i.descripcion)),
    descuentos: esAfp
      ? [
        resolver('afp_comision', '0601', 'SISTEMA PRIVADO DE PENSIONES - COMISIÓN'),
        resolver('afp_seguro', '0606', 'SISTEMA PRIVADO DE PENSIONES - PRIMA DE SEGURO'),
        resolver('afp', '0608', 'SISTEMA PRIVADO DE PENSIONES - APORTACIÓN OBLIGATORIA'),
        resolver('conafovicer', '0602', 'CONAFOVICER'),
      ]
      : [
        resolver('onp', '0607', 'SISTEMA NACIONAL DE PENSIONES - D.L 19990'),
        resolver('conafovicer', '0602', 'CONAFOVICER'),
      ],
    aportes: [
      resolver('essalud_empleador', '0804', 'ESSALUD (SEGURO REGULAR, CBSSP, AGRARIO/ACUICULTOR) - TRABAJADOR'),
    ],
  };
}

function lineaCatalogo(item, tipo) {
  return {
    codigo: item.codigo,
    codigo_plame: item.codigo,
    codigoPlame: item.codigo,
    descripcion: item.descripcion,
    concepto: item.descripcion,
    nombre: item.descripcion,
    monto: 0,
    tipo_concepto: tipo,
    origenCalculo: 'construccion_civil',
    plame_configuracion_id: item.plame_configuracion_id || null,
    plame_configuracion: item.plame_configuracion || null,
  };
}

export function completarCatalogoBoletaConstruccion(conceptos = {}, {
  sinTareos = false,
  sistemaPensionario = 'ONP',
  conceptosPlame = [],
  plameConsu = [],
} = {}) {
  const catalogo = catalogoDiarioConstruccion({ sistemaPensionario, conceptosPlame, plameConsu });
  if (sinTareos) {
    return {
      ingresos: catalogo.ingresos.map((i) => lineaCatalogo(i, 'ingreso')),
      descuentos: catalogo.descuentos.map((i) => lineaCatalogo(i, 'descuento')),
      aportes: catalogo.aportes.map((i) => lineaCatalogo(i, 'aporte')),
    };
  }

  const enriquecerConTabla = (c) => {
    const codigo = normalizarCodigoPlame(c.codigoPlame || c.codigo_plame || c.codigo);
    if (esCodigoInternoHorasExtraConsu(codigo)) return c;
    const fila = buscarEnTablaPlame(conceptosPlame, codigo);
    if (!fila) return c;
    return {
      ...c,
      codigo: fila.codigo_plame || codigo,
      codigo_plame: fila.codigo_plame || codigo,
      codigoPlame: fila.codigo_plame || codigo,
      descripcion: fila.descripcion || c.descripcion,
      concepto: fila.descripcion || c.concepto,
      nombre: fila.descripcion || c.nombre,
      plame_configuracion_id: fila.id,
      plame_configuracion: {
        id: fila.id,
        codigo_plame: fila.codigo_plame || codigo,
        descripcion: fila.descripcion,
      },
    };
  };

  const merge = (actuales, plantilla, tipo) => {
    const claveCodigo = (c) =>
      normalizarCodigoPlame(c?.codigoPlame || c?.codigo_plame || c?.codigo || '');
    const mapa = new Map();
    (actuales || []).forEach((c, idx) => {
      const enriquecido = enriquecerConTabla(c);
      const codigo = claveCodigo(enriquecido) || `tmp-${tipo}-${idx}`;
      const prev = mapa.get(codigo);
      const montoNuevo = Number(enriquecido.monto ?? enriquecido.resultado) || 0;
      const montoPrev = prev ? (Number(prev.monto ?? prev.resultado) || 0) : -1;
      if (!prev || montoNuevo >= montoPrev) {
        mapa.set(codigo, enriquecido);
      }
    });
    plantilla.forEach((item) => {
      const codigo = normalizarCodigoPlame(item.codigo);
      if (!codigo || mapa.has(codigo)) return;
      mapa.set(codigo, lineaCatalogo(item, tipo));
    });
    return Array.from(mapa.values());
  };

  return {
    ingresos: merge(conceptos.ingresos, catalogo.ingresos, 'ingreso'),
    descuentos: merge(conceptos.descuentos, catalogo.descuentos, 'descuento'),
    aportes: merge(conceptos.aportes, catalogo.aportes, 'aporte'),
  };
}

function lineaLegal({ clave, codigo, descripcion, monto, base, formula, tipo, plame = {}, conceptosPlame = [] }) {
  const meta = codigoPlame(plame, clave, codigo);
  const codigoFinal = meta.codigo || codigo;
  const fila = buscarEnTablaPlame(conceptosPlame, codigoFinal);
  const nombre = fila?.descripcion || descripcion;
  const codigoOficial = fila?.codigo_plame || codigoFinal;
  return {
    codigoPlame: codigoOficial,
    codigo_plame: codigoOficial,
    codigo: codigoOficial,
    concepto: nombre,
    descripcion: nombre,
    nombre,
    monto,
    base,
    base_calculo: aDosDecimales(base),
    formula,
    tipo_concepto: tipo,
    origenCalculo: 'construccion_civil',
    claveConstruccion: clave,
    plame_configuracion_id: fila?.id || null,
    plame_configuracion: fila ? {
      id: fila.id,
      codigo_plame: fila.codigo_plame || codigoOficial,
      descripcion: fila.descripcion,
    } : null,
  };
}

export function aportesLegalesConstruccion({
  bases,
  parametro,
  sistemaPensionario,
  tasaAfp = 0,
  afpRegistro = null,
  tipoAfp = 'Flujo',
  fechaNacimiento = null,
  plame = {},
  conceptosPlame = [],
}) {
  const onp = String(sistemaPensionario || '').toUpperCase().includes('ONP')
    || String(sistemaPensionario || '').includes('19990')
    || String(sistemaPensionario || '').toLowerCase().includes('nacional');
  const esAfp = String(sistemaPensionario || '').toUpperCase().includes('AFP');
  const descuentos = [];
  const aportes = [];
  const extras = { plame, conceptosPlame };
  const basePension = bases.onp_afp;

  if (onp) {
    descuentos.push(lineaLegal({
      clave: 'onp',
      codigo: '0607',
      descripcion: 'SISTEMA NACIONAL DE PENSIONES - D.L 19990',
      monto: aDosDecimales(multiplicarDinero(basePension, 0.13)),
      base: basePension,
      formula: '13% sobre jornal + DSO/feriado + BUC (+ vacaciones si van en la semana)',
      tipo: 'descuento',
      ...extras,
    }));
  } else if (esAfp) {
    const tasas = afpRegistro
      ? tasasAfpConstruccion(afpRegistro, tipoAfp)
      : { comision: 0, seguro: 0, aporte: tasaAfp > 0 ? tasaAfp : 0.1 };
    const edad = edadEmpleadoDesdeFechaNacimiento(fechaNacimiento);

    if (tasas.comision > 0) {
      descuentos.push(lineaLegal({
        clave: 'afp_comision',
        codigo: '0601',
        descripcion: 'SISTEMA PRIVADO DE PENSIONES - COMISIÓN',
        monto: aDosDecimales(multiplicarDinero(basePension, tasas.comision)),
        base: basePension,
        formula: `${aDosDecimales(tasas.comision * 100)}% comisión AFP (0601) sobre base pensionable CONSU`,
        tipo: 'descuento',
        ...extras,
      }));
    }

    if (tasas.seguro > 0 && !(edad !== null && edad >= 65)) {
      descuentos.push(lineaLegal({
        clave: 'afp_seguro',
        codigo: '0606',
        descripcion: 'SISTEMA PRIVADO DE PENSIONES - PRIMA DE SEGURO',
        monto: aDosDecimales(multiplicarDinero(basePension, tasas.seguro)),
        base: basePension,
        formula: `${aDosDecimales(tasas.seguro * 100)}% seguro AFP (0606) sobre base pensionable CONSU`,
        tipo: 'descuento',
        ...extras,
      }));
    }

    if (tasas.aporte > 0) {
      descuentos.push(lineaLegal({
        clave: 'afp',
        codigo: '0608',
        descripcion: 'SISTEMA PRIVADO DE PENSIONES - APORTACIÓN OBLIGATORIA',
        monto: aDosDecimales(multiplicarDinero(basePension, tasas.aporte)),
        base: basePension,
        formula: `${aDosDecimales(tasas.aporte * 100)}% aporte obligatorio AFP (0608) sobre base pensionable CONSU`,
        tipo: 'descuento',
        ...extras,
      }));
    }
  }

  descuentos.push(lineaLegal({
    clave: 'conafovicer',
    codigo: '0602',
    descripcion: 'CONAFOVICER',
    monto: aDosDecimales(multiplicarDinero(bases.conafovicer, Number(parametro.conafovicer_porcentaje || 0.02))),
    base: bases.conafovicer,
    formula: `${aDosDecimales((parametro.conafovicer_porcentaje || 0.02) * 100)}% sobre jornal + DSO`,
    tipo: 'descuento',
    ...extras,
  }));

  aportes.push(lineaLegal({
    clave: 'essalud_empleador',
    codigo: '0804',
    descripcion: 'ESSALUD (SEGURO REGULAR, CBSSP, AGRARIO/ACUICULTOR) - TRABAJADOR',
    monto: aDosDecimales(multiplicarDinero(bases.essalud, 0.09)),
    base: bases.essalud,
    formula: '9% empleador. No reduce el neto del trabajador',
    tipo: 'aporte',
    ...extras,
  }));

  return { descuentos, aportes };
}
